import { useCallback, useEffect, useRef, useState } from "react";
import { fetchGreenieBoard } from "../services/greenieBoardService";
import { GreenieBoardFilters, GreeniePilot, GreenieServer } from "../types/greenieBoard";

export interface UseGreenieBoardResult {
  pilots: GreeniePilot[];
  airframes: string[];
  servers: GreenieServer[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export default function useGreenieBoard(filters: GreenieBoardFilters = {}): UseGreenieBoardResult {
  const [pilots, setPilots] = useState<GreeniePilot[]>([]);
  const [airframes, setAirframes] = useState<string[]>([]);
  const [servers, setServers] = useState<GreenieServer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchGreenieBoard(filters);
      if (id === requestIdRef.current) {
        setPilots(data.pilots);
        setAirframes(data.airframes);
        setServers(data.servers);
      }
    } catch (err) {
      if (id === requestIdRef.current) {
        setError(err instanceof Error ? err.message : "Unexpected error");
      }
    } finally {
      if (id === requestIdRef.current) {
        setLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.pilot_uid, filters.unit_family, filters.server_id, filters.period, filters.sort]);

  useEffect(() => {
    load();
  }, [load]);

  return { pilots, airframes, servers, loading, error, refresh: load };
}
